import { useState, useEffect, useCallback, useRef } from 'react'
import { ShieldAlert, ShieldCheck, Lock, Unlock, Loader2, Clock3, KeyRound } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

function formatRemaining(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return h + 'h ' + m + 'm ' + s + 's'
  if (m > 0) return m + 'm ' + s + 's'
  return s + 's'
}

interface TierState {
  active: boolean | null
  expiresAt: string | null
  remaining: number
  hours: number
  totpCode: string
  busy: boolean
  error: string | null
}

const initialTierState: TierState = {
  active: null,
  expiresAt: null,
  remaining: 0,
  hours: 8,
  totpCode: '',
  busy: false,
  error: null,
}

const TIER_COPY = {
  2: {
    label: 'Tier 2 Override',
    body: 'Temporarily enables Tier 2 actions without a separate approval tap while the window is active.',
    accent: 'blue',
  },
  3: {
    label: 'Tier 3 Override',
    body: 'Highest-privilege operational window for deliberately unattended Tier 3 work.',
    accent: 'violet',
  },
} as const

const DURATION_OPTIONS = [1, 4, 8, 12]

export function Tier3OverridePanel({ embedded = false }: { embedded?: boolean }) {
  const [tiers, setTiers] = useState<Record<2 | 3, TierState>>({ 2: initialTierState, 3: initialTierState })
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function patchTier(tier: 2 | 3, patch: Partial<TierState>) {
    setTiers(prev => ({ ...prev, [tier]: { ...prev[tier], ...patch } }))
  }

  const fetchStatus = useCallback(async () => {
    try {
      const data = await api.getTierOverrideStatus()
      setTiers(prev => ({
        2: { ...prev[2], active: data.tier2.active, expiresAt: data.tier2.expires_at || null, remaining: data.tier2.remaining_seconds || 0, error: null },
        3: { ...prev[3], active: data.tier3.active, expiresAt: data.tier3.expires_at || null, remaining: data.tier3.remaining_seconds || 0, error: null },
      }))
    } catch {
      setTiers(prev => ({
        2: { ...prev[2], error: 'Could not reach OPS to check override status' },
        3: { ...prev[3], error: 'Could not reach OPS to check override status' },
      }))
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    pollRef.current = setInterval(fetchStatus, 30_000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [fetchStatus])

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current)
    if (tiers[2].active || tiers[3].active) {
      tickRef.current = setInterval(() => {
        setTiers(prev => ({
          2: prev[2].active ? { ...prev[2], remaining: Math.max(0, prev[2].remaining - 1) } : prev[2],
          3: prev[3].active ? { ...prev[3], remaining: Math.max(0, prev[3].remaining - 1) } : prev[3],
        }))
      }, 1000)
    }
    return () => { if (tickRef.current) clearInterval(tickRef.current) }
  }, [tiers[2].active, tiers[3].active])

  async function handleStart(tier: 2 | 3) {
    const state = tiers[tier]
    if (!/^\d{6}$/.test(state.totpCode)) {
      patchTier(tier, { error: 'Enter your live 6-digit authenticator code' })
      return
    }
    patchTier(tier, { busy: true, error: null })
    try {
      const result = await api.setTierOverride(tier, state.hours, state.totpCode)
      patchTier(tier, {
        active: true,
        expiresAt: result.expires_at,
        remaining: Math.round(state.hours * 3600),
        totpCode: '',
        busy: false,
      })
    } catch (err: unknown) {
      patchTier(tier, {
        error: err instanceof Error ? err.message : 'Could not start the override window — check the code',
        busy: false,
      })
    }
  }

  async function handleStop(tier: 2 | 3) {
    const state = tiers[tier]
    if (!/^\d{6}$/.test(state.totpCode)) {
      patchTier(tier, { error: 'Enter your live 6-digit authenticator code' })
      return
    }
    patchTier(tier, { busy: true, error: null })
    try {
      await api.clearTierOverride(tier, state.totpCode)
      patchTier(tier, { active: false, expiresAt: null, remaining: 0, totpCode: '', busy: false })
    } catch (err: unknown) {
      patchTier(tier, {
        error: err instanceof Error ? err.message : 'Could not close the override window — check the code',
        busy: false,
      })
    }
  }

  function renderTier(tier: 2 | 3) {
    const state = tiers[tier]
    const copy = TIER_COPY[tier]
    const active = state.active === true

    return (
      <section className={cn('ops-override-card', copy.accent === 'violet' && 'ops-override-card-violet')}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className={cn('ops-override-shield', copy.accent === 'violet' && 'ops-override-shield-violet')}>
              {active ? <ShieldAlert className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
            </span>
            <div>
              <h3 className="text-[17px] font-semibold text-text-primary">{copy.label}</h3>
              <p className="text-xs text-text-muted mt-1 max-w-xl">{copy.body}</p>
            </div>
          </div>
          <span className={cn('ops-override-status', active && 'ops-override-status-open')}>
            {active ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {state.active === null ? 'Checking…' : active ? 'Open' : 'Locked'}
          </span>
        </div>

        {state.active === null ? (
          <div className="flex items-center justify-center py-10 text-xs text-text-muted gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking status…
          </div>
        ) : active ? (
          <div className="mt-5 space-y-4">
            <div className="ops-override-countdown">
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <Clock3 className="h-4 w-4" /> Self-expiring window
              </div>
              <div className="text-2xl font-semibold tabular-nums mt-1">{formatRemaining(state.remaining)}</div>
              <div className="text-[10px] text-text-muted mt-1">
                {state.expiresAt ? 'Closes automatically at ' + new Date(state.expiresAt).toLocaleString() : 'Auto-lock enabled'}
              </div>
            </div>

            <label className="ops-override-input">
              <KeyRound className="h-4 w-4" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Code to close (TOTP)"
                value={state.totpCode}
                onChange={e => patchTier(tier, { totpCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              />
            </label>

            <button
              onClick={() => handleStop(tier)}
              disabled={state.busy}
              className="ops-override-stop"
            >
              {state.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Stop & Lock Now
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div>
              <div className="text-[11px] font-medium text-text-secondary mb-2">Select duration</div>
              <div className="ops-duration-grid">
                {DURATION_OPTIONS.map(hours => (
                  <button
                    key={hours}
                    onClick={() => patchTier(tier, { hours })}
                    className={cn('ops-duration-option', state.hours === hours && 'ops-duration-option-active')}
                  >
                    {hours + 'h'}
                  </button>
                ))}
              </div>
            </div>

            <label className="ops-override-input">
              <KeyRound className="h-4 w-4" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Code to start (TOTP)"
                value={state.totpCode}
                onChange={e => patchTier(tier, { totpCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              />
            </label>

            <button
              onClick={() => handleStart(tier)}
              disabled={state.busy}
              className={cn('ops-override-start', copy.accent === 'violet' && 'ops-override-start-violet')}
            >
              {state.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
              Open {copy.label} for {state.hours}h
            </button>

            <div className="flex items-center gap-2 text-[10px] text-text-muted">
              <Clock3 className="h-3.5 w-3.5" />
              Override automatically expires after the selected duration.
            </div>
          </div>
        )}

        {state.error && <div className="mt-3 text-[11px] text-red-500">{state.error}</div>}
      </section>
    )
  }

  return (
    <div className={cn('space-y-4', !embedded && 'max-w-5xl mx-auto')}>
      <div className="ops-governance-note">
        <div className="flex items-start gap-3">
          <span className="ops-icon-tile ops-icon-tile-item"><ShieldCheck className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-semibold text-text-primary">Approval Override</div>
            <p className="text-xs text-text-muted mt-1 leading-relaxed">
              Two independent, self-expiring windows. Either, both, or neither may be open.
              Each requires its own live authenticator code to open or close every time.
            </p>
          </div>
        </div>
      </div>

      {renderTier(2)}
      {renderTier(3)}

      <p className="text-[10px] text-text-muted text-center px-4">
        Human-only by design (AD-039/AD-040): AI tooling cannot start or stop either override window.
      </p>
    </div>
  )
}
